import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {action, computed, makeAutoObservable, observable} from "mobx";
import {ModalType, RequestState, RoundStatus, TeamRegion} from "data/enums";
import {Bindings} from "data/constants/bindings";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IUserStore} from "data/stores/user/user.store";
import type {IPlayer, IPlayersStore, IFantasyData} from "data/stores/players/players.store";
import type {ITeamStore} from "data/stores/team/team.store";
import {eq, every, get, includes} from "lodash";
import type {IPlayerPoolStore} from "data/stores/player_pool/player_pool.store";
import type {IGolfersData, IRoundsStore, ISession} from "data/stores/rounds/rounds.store";
import {ITeamResponse} from "data/providers/api/team.api.provider";
import type {IModalsStore} from "data/stores/modals/modals.store";

interface IControllerProps {
	playerId: number;
	roundId: number;
}

export interface IPlayerSlotController extends ViewController<IControllerProps> {
	get i18n(): ILocalizationStore;

	get player(): IPlayer | undefined;

	get isCaptain(): boolean;

	get selectedSession(): ISession | undefined;

	get userTeamSelectedSession(): ISession | undefined;

	get golfersData(): IGolfersData | undefined;

	get selectedSessionId(): number;

	get userTeamsSelectedSessionId(): number;

	get golfersArr(): IGolfersData[];

	get partnerId(): number | undefined;

	get opponentData(): number[];

	get isTradeStarted(): boolean;

	get isTradeOpen(): boolean;

	get teamObject(): ITeamResponse;

	get isAllTradesDone(): boolean;

	get result(): string;

	get matchResult(): {
		className: string;
		arrowClass: string;
		circle_label: string;
		hole_no: string;
		team_standing: string;
		match_status: RoundStatus;
	};

	get notPlaying(): boolean;

	playerLastNameById: (playerId: number | undefined) => string;
	onRemovePlayer: () => void;
	onOpenPoolModal: () => void;
	onSelectCaptain: () => void;
	onPlayerClick: () => void;
	setRoundId: (roundId: number) => void;
}

@injectable()
export class PlayerSlotController implements IPlayerSlotController {
	@observable _requestState: RequestState = RequestState.IDLE;
	@observable private _playerId!: number;
	@observable private _roundId?: number;

	get i18n() {
		return this._i18nStore;
	}

	get teamObject() {
		return this._teamStore.teamObject;
	}

	get player() {
		return this._playerStore.getPlayerById(this._playerId);
	}

	get isCaptain() {
		return eq(this._playerId, this._teamStore.lineup.captain);
	}

	get selectedSession() {
		return this._roundStore.selectedSession;
	}

	get selectedSessionId() {
		return this._roundStore.selectedSessionId;
	}

	get userTeamsSelectedSessionId() {
		return this._roundStore.userTeamsSelectedSessionId;
	}

	get userTeamSelectedSession() {
		return this._roundStore.userTeamSelectedSession;
	}

	get round() {
		if (!this._roundId) {
			return null;
		}

		return this._roundStore.getRoundById(this._roundId);
	}

	get golfersArr() {
		return this.round?.golfers || [];
	}

	@computed get golfersData() {
		return this.golfersArr.find((el) =>
			includes([...(el.usa ?? []), ...(el.europe ?? [])], this._playerId)
		);
	}

	get partnerId() {
		const usaGolfers = get(this.golfersData, "usa", []);
		const europeGolfers = get(this.golfersData, "europe", []);
		const isUsaPlayer = eq(this.player?.team, TeamRegion.Usa);

		return isUsaPlayer
			? usaGolfers.find((el) => el !== this._playerId)
			: europeGolfers.find((el) => el !== this._playerId);
	}

	get opponentData() {
		const usaGolfers = get(this.golfersData, "usa", []);
		const europeGolfers = get(this.golfersData, "europe", []);
		const isUsaPlayer = eq(this.player?.team, TeamRegion.Usa);

		return isUsaPlayer ? europeGolfers : usaGolfers;
	}

	get isTradeStarted() {
		return this._teamStore.isTradeStarted;
	}

	get isTradeOpen() {
		return every([this._roundStore.isPMSession, this._teamStore.isTradeStarted]);
	}

	get isAllTradesDone() {
		return this._teamStore.isAllTradesDone;
	}

	get fantasyStats() {
		if (!this._roundId) {
			return null;
		}

		return get(this.player?.fantasy, this._roundId, {} as IFantasyData);
	}

	get result() {
		return get(this.fantasyStats, "result", "").replace(/\s/g, "").replace("T", "TIED");
	}

	get matchResult() {
		const holeNo = get(this.fantasyStats, "holeNo", "");
		const teamStanding = get(this.fantasyStats, "teamStanding", "");
		const matchStatus = get(this.fantasyStats, "matchStatus", RoundStatus.Scheduled);

		const winMatchValue = get(this.fantasyStats, "win_match", 0);
		const loseMatchValue = get(this.fantasyStats, "lost_match", 0);

		const isWin = winMatchValue > 0;
		const isLose = loseMatchValue > 0;

		const isArrowUp = includes(teamStanding, "UP");
		const isArrowDn = includes(teamStanding, "DN");

		const getArrowClass = () => {
			return isArrowUp ? "win" : isArrowDn ? "lose" : "tied";
		};

		return isWin
			? {
					className: "win",
					arrowClass: getArrowClass(),
					circle_label: this.i18n.t("player_profile.session.win", "W"),
					hole_no: holeNo,
					team_standing: teamStanding,
					match_status: matchStatus,
			  }
			: isLose
			? {
					className: "lose",
					arrowClass: getArrowClass(),
					circle_label: this.i18n.t("player_profile.session.lose", "L"),
					hole_no: holeNo,
					team_standing: teamStanding,
					match_status: matchStatus,
			  }
			: {
					className: "tied",
					arrowClass: getArrowClass(),
					circle_label: this.i18n.t("player_profile.session.tied", "T"),
					hole_no: holeNo,
					team_standing: teamStanding,
					match_status: matchStatus,
			  };
	}

	get notPlaying() {
		return every([!this.golfersData, this.golfersArr.length]);
	}

	constructor(
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.LocalizationStore) private _i18nStore: ILocalizationStore,
		@inject(Bindings.PlayerPoolStore) private _poolStore: IPlayerPoolStore,
		@inject(Bindings.PlayersStore) private _playerStore: IPlayersStore,
		@inject(Bindings.RoundsStore) private _roundStore: IRoundsStore,
		@inject(Bindings.TeamStore) private _teamStore: ITeamStore,
		@inject(Bindings.ModalsStore) private _modalsStore: IModalsStore
	) {
		makeAutoObservable(this);
	}

	playerLastNameById = (playerId: number | undefined) => {
		if (playerId) {
			const player = this._playerStore.getPlayerById(playerId);

			return player ? player.lastName : "";
		}
		return "";
	};

	onRemovePlayer = () => {
		if (this._teamStore.isAllTradesDone) return;

		if (this.isTradeOpen) {
			this._teamStore.setPlayerOut(this._playerId);
		}

		if (this.isCaptain) {
			this._teamStore.selectCaptain(0);
		}

		this._teamStore.removePlayer(this._playerId);
	};

	onOpenPoolModal = () => {
		this._poolStore.togglePoolModal();
	};

	onSelectCaptain = () => {
		if (this.isTradeOpen) {
			if (this._teamStore.tradesArr.length) {
				this._teamStore.selectCaptain(this._playerId);
				return;
			}

			this._teamStore.selectCaptain(this._playerId);
			this._modalsStore.showModal(ModalType.CHANGE_CAPTAIN, {
				message: "",
				playerId: this._playerId,
			});
		} else {
			this._teamStore.selectCaptain(this._playerId);
		}
	};

	onPlayerClick = () => {
		this._modalsStore.showModal(ModalType.PLAYER_PROFILE, {
			message: "",
			playerId: this._playerId,
		});
	};

	@action setRoundId = (roundId: number) => {
		this._roundId = roundId;
	};

	dispose() {
		return;
	}

	init({playerId, roundId}: IControllerProps) {
		this._playerId = playerId;

		this.setRoundId(roundId);
	}
}
